import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from 'hooks';
import { useDispatch, useSelector } from 'store';
import { myBirthdayPopupReceived } from 'store/actions/authActions';
import { getBirthdayPopupSettings } from 'lib/apiActions';
import { fetchMedia, getBirthdayPopupSeenKey } from 'lib/helper';

export function useMyBirthdayModal() {
  const { t } = useTranslation();
  const { userId, networkId, token } = useAuth();
  const dispatch = useDispatch();

  const showMyBirthdayModal = useSelector((state: RootState) => state.auth.showMyBirthdayPopup);
  const myBirthdayName = useSelector((state: RootState) => state.auth.myBirthdayName);

  const [settings, setSettings] = useState<BirthdayPopupSettings | null>(null);

  // Busca a configuração da rede assim que possível, independente de quando o
  // popup for de fato exibido — o disparo em si só depende do flag do Redux
  // (setado pelo push NewBirthdays), então quando ele chegar a config já está pronta.
  useEffect(() => {
    if (!networkId) return;

    getBirthdayPopupSettings(networkId).then(result => {
      setSettings(
        result ?? { networkId, mode: 'default', message: null, templateId: null, mediaId: null }
      );
    });
  }, [networkId]);

  const closeMyBirthdayModal = useCallback(() => {
    dispatch(myBirthdayPopupReceived(false));

    // Marca o dia como "já visto" pra não reabrir se o mesmo push chegar de
    // novo (reentrega, outra aba) — exigido pelo escopo original da feature.
    try {
      localStorage.setItem(getBirthdayPopupSeenKey(userId), '1');
    } catch {
      // ignora erro de storage (navegação privada, cota, etc.)
    }
  }, [dispatch, userId]);

  const mode: BirthdayPopupMode = settings?.mode ?? 'default';
  const title: string = myBirthdayName
    ? t('birthday.myBirthdayTitleWithName', { name: myBirthdayName })
    : t('birthday.myBirthdayTitle');
  const message: string = settings?.message ? settings.message : t('birthday.myBirthdayMessage');
  const imageUrl =
    mode === 'custom' && settings?.mediaId ? fetchMedia(settings.mediaId, token) : null;

  return {
    showMyBirthdayModal,
    closeMyBirthdayModal,
    mode,
    title,
    message,
    templateId: settings?.templateId,
    imageUrl,
  };
}
